import type { SupabaseClient } from '@supabase/supabase-js';
import { createLogger } from '@/lib/logger';
import { supabase } from './supabase';
import { events, EventTypes } from './events';
import { getAnalyticsDateRange, type TimeRange } from './analyticsRange';
import { calculateWorkoutVolume } from './volumeCalc';
import { calculateV1Score, type LiftInput } from './v1Scoring';
import { V1_LIFT_POOL, type V1Lift, normalizeToV1Lift, calculateE1RM, calculateDeadHangScore } from './lifts/canonical';
import * as cache from './cache';
import { dedup } from './dedup';
import type { OrgLeaderboardEntry } from '@/types/database';

const log = createLogger('LEADERBOARD');

export type LeaderboardRowV1 = {
  user_id: string;
  handle: string | null;
  display_name: string | null;
  avatar_url: string | null;
  gym_id: string | null;
  strength_score: number | null;
  freshness_days: number | null;
  freshness_band: string | null;
  wins: number | null;
  losses: number | null;
  total_duels: number | null;
  rank: number | null;
  gender: string | null;
  gym_name: string | null;
  total_volume: number | null;
  bodyweight?: number | null;
  height_cm?: number | null;
  age?: number | null;
  visibility_weight?: boolean | null;
  visibility_height?: boolean | null;
  visibility_age?: boolean | null;
  created_at?: string | null;
};

export type LeaderboardFetchResult = {
  rows: LeaderboardRowV1[];
  error: string | null;
  hasMore?: boolean;
};

const LEADERBOARD_SELECT =
  'user_id, handle, display_name, avatar_url, gym_id, strength_score, freshness_days, freshness_band, wins, losses, total_duels, rank, bodyweight, gender, created_at, total_volume';

/** Compute raw volume (weight * reps) per user for a given time range, normalized to targetUnits */
async function computeVolumeForUsers(
  client: SupabaseClient,
  userIds: string[],
  timePeriod?: TimeRange,
  targetUnits: string = 'lbs',
): Promise<Map<string, number>> {
  const volumeMap = new Map<string, number>();
  if (userIds.length === 0) return volumeMap;

  const range = getAnalyticsDateRange(timePeriod ?? 'lifetime', false);

  let query = client
    .from('workouts')
    .select('user_id, payload')
    .in('user_id', userIds);

  if (range.startUtcIso) query = query.gte('performed_at', range.startUtcIso);
  query = query.lt('performed_at', range.endUtcIsoExclusive);

  const { data: workouts } = await query;
  if (!workouts) return volumeMap;

  workouts.forEach((w: any) => {
    const vol = calculateWorkoutVolume(w.payload, targetUnits);
    volumeMap.set(w.user_id, (volumeMap.get(w.user_id) ?? 0) + vol);
  });

  return volumeMap;
}

async function enrichRows(client: SupabaseClient, rows: LeaderboardRowV1[], timePeriod?: TimeRange, viewerUnits: string = 'lbs'): Promise<LeaderboardRowV1[]> {
  if (rows.length === 0) return rows;
  try {
    const ids = rows.map((r) => r.user_id);
    const { data } = await client.from('profiles').select('id, gender, home_gym_name, lifting_score, duel_standard_strength_kg, created_at').in('id', ids);
    if (data) {
      const profileMap = new Map<string, { gender: string | null; gym_name: string | null; lifting_score: number | null; strength: number | null; created_at: string | null }>();
      (data as any[]).forEach((p) => {
        profileMap.set(p.id, {
          gender: p.gender ?? null,
          gym_name: p.home_gym_name ?? null,
          lifting_score: p.lifting_score ?? null,
          strength: p.duel_standard_strength_kg ?? null,
          created_at: p.created_at ?? null,
        });
      });
      // Compute actual volume from workouts for the time period
      const volumeMap = await computeVolumeForUsers(client, ids, timePeriod, viewerUnits);

      // Fetch wins/losses from completed duels
      const winsMap = new Map<string, { wins: number; losses: number }>();
      try {
        const { data: duelData } = await client
          .from('duels')
          .select('winner_user_id, loser_user_id')
          .eq('status', 'completed')
          .or(ids.map(id => `winner_user_id.eq.${id},loser_user_id.eq.${id}`).join(','));
        if (duelData) {
          (duelData as any[]).forEach((d) => {
            if (d.winner_user_id && ids.includes(d.winner_user_id)) {
              const cur = winsMap.get(d.winner_user_id) ?? { wins: 0, losses: 0 };
              winsMap.set(d.winner_user_id, { ...cur, wins: cur.wins + 1 });
            }
            if (d.loser_user_id && ids.includes(d.loser_user_id)) {
              const cur = winsMap.get(d.loser_user_id) ?? { wins: 0, losses: 0 };
              winsMap.set(d.loser_user_id, { ...cur, losses: cur.losses + 1 });
            }
          });
        }
      } catch { /* non-blocking */ }

      return rows.map((r) => {
        const info = profileMap.get(r.user_id);
        const record = winsMap.get(r.user_id);
        return {
          ...r,
          // Override NULL strength_score from DB view with actual value from profiles
          strength_score: info?.strength ?? r.strength_score ?? null,
          gender: info?.gender ?? null,
          gym_name: info?.gym_name ?? null,
          total_volume: volumeMap.get(r.user_id) ?? 0,
          created_at: info?.created_at ?? null,
          wins: record?.wins ?? r.wins ?? null,
          losses: record?.losses ?? r.losses ?? null,
          total_duels: ((record?.wins ?? 0) + (record?.losses ?? 0)) || r.total_duels || null,
        };
      });
    }
  } catch { /* columns may not exist yet */ }
  return rows;
}

/** Build leaderboard rows directly from profiles table (fallback when views fail or return empty) */
async function fetchFromProfiles(
  client: SupabaseClient,
  gymId?: string | null,
  limit: number = 100,
  offset: number = 0,
): Promise<LeaderboardRowV1[]> {
  let query = client
    .from('profiles')
    .select('id, handle, display_name, avatar_url, gym_id, home_gym_id, duel_standard_strength_kg, lifting_score, gender, home_gym_name, bodyweight, height_cm, visibility_weight, visibility_height, visibility_age, created_at')
    .not('handle', 'is', null)
    .order('duel_standard_strength_kg', { ascending: false, nullsFirst: false })
    .range(offset, offset + limit - 1);

  if (gymId) {
    query = query.or(`gym_id.eq.${gymId},home_gym_id.eq.${gymId}`);
  }

  const { data } = await query;
  if (!data) return [];

  return (data as any[]).map((p, i) => ({
    user_id: p.id,
    handle: p.handle,
    display_name: p.display_name,
    avatar_url: p.avatar_url,
    gym_id: p.gym_id || p.home_gym_id,
    strength_score: p.duel_standard_strength_kg ?? p.lifting_score ?? null,
    freshness_days: null,
    freshness_band: null,
    wins: null,
    losses: null,
    total_duels: null,
    rank: offset + i + 1,
    gender: p.gender ?? null,
    gym_name: p.home_gym_name ?? null,
    total_volume: null,
    bodyweight: p.bodyweight ?? null,
    height_cm: p.height_cm ?? null,
    age: null,
    visibility_weight: p.visibility_weight ?? false,
    visibility_height: p.visibility_height ?? false,
    visibility_age: p.visibility_age ?? false,
    created_at: p.created_at ?? null,
  }));
}

/** Map a materialized view row to LeaderboardRowV1 shape */
function mapMvRow(mv: any): LeaderboardRowV1 {
  return {
    user_id: mv.id,
    handle: mv.handle ?? null,
    display_name: mv.display_name ?? null,
    avatar_url: mv.avatar_url ?? null,
    gym_id: mv.gym_id ?? null,
    strength_score: mv.duel_standard_strength_kg ?? null,
    freshness_days: null,
    freshness_band: mv.freshness_band ?? null,
    wins: mv.duels_won ?? null,
    losses: mv.duels_total != null && mv.duels_won != null ? (mv.duels_total - mv.duels_won) : null,
    total_duels: mv.duels_total ?? null,
    rank: mv.rank ?? null,
    gender: mv.gender ?? null,
    gym_name: null,
    total_volume: null,
    bodyweight: mv.bodyweight ?? null,
    height_cm: null,
    age: null,
    visibility_weight: null,
    visibility_height: null,
    visibility_age: null,
    created_at: null,
  };
}

export async function fetchLeaderboardGlobalV1(
  client: SupabaseClient = supabase,
  opts?: { limit?: number; offset?: number; timePeriod?: TimeRange; viewerUnits?: string }
): Promise<LeaderboardFetchResult> {
  const limit = opts?.limit ?? 100;
  const offset = opts?.offset ?? 0;

  const cacheKey = `lb:global:${offset}:${limit}:${opts?.timePeriod ?? ''}`;
  const cached = await cache.get<LeaderboardFetchResult>(cacheKey);
  if (cached) return cached;

  return dedup(cacheKey, async () => {
    try {
      // --- Try materialized view first ---
      try {
        const { data: mvData, error: mvError } = await client
          .from('mv_leaderboard_global')
          .select('id, handle, display_name, avatar_url, gender, duel_standard_strength_kg, bodyweight, gym_id, freshness_band, units, rank, duels_won, duels_total')
          .order('rank', { ascending: true })
          .range(offset, offset + limit - 1);

        if (!mvError && mvData && mvData.length > 0) {
          const rows: LeaderboardRowV1[] = (mvData as any[]).map(mapMvRow);
          const enriched = await enrichRows(client, rows, opts?.timePeriod, opts?.viewerUnits);
          const result: LeaderboardFetchResult = { rows: enriched, error: null, hasMore: enriched.length >= limit };
          await cache.set(cacheKey, result, 300_000);
          return result;
        }
      } catch { /* MV not available, fall through */ }

      // --- Fall back to live view ---
      const { data, error } = await client
        .from('public_leaderboard_global_v1')
        .select(LEADERBOARD_SELECT)
        .order('rank', { ascending: true })
        .order('strength_score', { ascending: false })
        .order('user_id', { ascending: true })
        .range(offset, offset + limit - 1);

      if (error) {
        const msg = String(error.message ?? '');
        const isMissing = error.code === '42P01' || /does not exist/i.test(msg);
        if (isMissing) {
          const fallback = await fetchFromProfiles(client, null, limit, offset);
          const enriched = await enrichRows(client, fallback, opts?.timePeriod, opts?.viewerUnits);
          const result = { rows: enriched, error: null };
          await cache.set(cacheKey, result, 60_000);
          return result;
        }
        return { rows: [], error: 'Unable to load leaderboard' };
      }

      let rows = (data as LeaderboardRowV1[]) ?? [];
      if (rows.length === 0 && offset === 0) {
        rows = await fetchFromProfiles(client, null, limit, offset);
      }

      const enriched = await enrichRows(client, rows, opts?.timePeriod, opts?.viewerUnits);
      const result: LeaderboardFetchResult = { rows: enriched, error: null, hasMore: enriched.length >= limit };
      await cache.set(cacheKey, result, 60_000);
      return result;
    } catch {
      return { rows: [], error: 'Unable to load leaderboard' };
    }
  });
}

export async function fetchLeaderboardGymV1(
  client: SupabaseClient = supabase,
  opts: { gymId: string; limit?: number; offset?: number; timePeriod?: TimeRange; viewerUnits?: string }
): Promise<LeaderboardFetchResult> {
  const limit = opts?.limit ?? 100;
  const offset = opts?.offset ?? 0;
  if (!opts?.gymId) return { rows: [], error: null };

  const cacheKey = `lb:gym:${opts.gymId}:${offset}:${limit}:${opts?.timePeriod ?? ''}`;
  const cached = await cache.get<LeaderboardFetchResult>(cacheKey);
  if (cached) return cached;

  return dedup(cacheKey, async () => {
    try {
      // --- Try materialized view first ---
      try {
        const { data: mvData, error: mvError } = await client
          .from('mv_leaderboard_gym')
          .select('id, handle, display_name, avatar_url, gender, duel_standard_strength_kg, bodyweight, gym_id, freshness_band, units, rank, duels_won, duels_total')
          .eq('gym_id', opts.gymId)
          .order('rank', { ascending: true })
          .range(offset, offset + limit - 1);

        if (!mvError && mvData && mvData.length > 0) {
          const rows: LeaderboardRowV1[] = (mvData as any[]).map(mapMvRow);
          const enriched = await enrichRows(client, rows, opts?.timePeriod, opts?.viewerUnits);
          const result: LeaderboardFetchResult = { rows: enriched, error: null, hasMore: enriched.length >= limit };
          await cache.set(cacheKey, result, 300_000);
          return result;
        }
      } catch { /* MV not available, fall through */ }

      // --- Fall back to live view ---
      const { data, error } = await client
        .from('public_leaderboard_gym_v1')
        .select(LEADERBOARD_SELECT)
        .eq('gym_id', opts.gymId)
        .order('rank', { ascending: true })
        .order('strength_score', { ascending: false })
        .order('user_id', { ascending: true })
        .range(offset, offset + limit - 1);

      if (error) {
        const msg = String(error.message ?? '');
        const isMissing = error.code === '42P01' || /does not exist/i.test(msg);
        if (isMissing) {
          const fallback = await fetchFromProfiles(client, opts.gymId, limit, offset);
          const enriched = await enrichRows(client, fallback, opts?.timePeriod, opts?.viewerUnits);
          const result = { rows: enriched, error: null };
          await cache.set(cacheKey, result, 60_000);
          return result;
        }
        return { rows: [], error: 'Unable to load leaderboard' };
      }

      let rows = (data as LeaderboardRowV1[]) ?? [];
      if (rows.length === 0 && offset === 0) {
        rows = await fetchFromProfiles(client, opts.gymId, limit, offset);
      }

      const enriched = await enrichRows(client, rows, opts?.timePeriod, opts?.viewerUnits);
      const result: LeaderboardFetchResult = { rows: enriched, error: null, hasMore: enriched.length >= limit };
      await cache.set(cacheKey, result, 60_000);
      return result;
    } catch {
      return { rows: [], error: 'Unable to load leaderboard' };
    }
  });
}

/**
 * Recompute leaderboard points for a user using the V1 Scoring Engine.
 *
 * Source of truth: lib/v1Scoring.ts `calculateV1Score`
 *
 * Steps:
 *   1. Fetch the user's best (most recent) strength_event per V1 lift
 *   2. Compute raw score per lift (e1RM for barbell lifts, BW×seconds for dead hang)
 *   3. Feed through calculateV1Score (BW-normalized, freshness-weighted, capped at 25% per lift)
 *   4. Persist the composite score to profiles
 *
 * @param userId - The user ID to recompute points for
 * @param trigger - The event that triggered this recompute (for logging)
 * @returns The computed total score, or null if computation failed
 */
export async function recomputeLeaderboardPoints(
  userId: string,
  trigger: 'workout_save' | 'workout_delete' | 'exercise_delete' | 'manual' = 'manual'
): Promise<number | null> {
  try {
    log.info('[LeaderboardRecompute] Starting V1 scoring...', { userId, trigger });

    // Fetch profile bodyweight
    const { data: profileData } = await supabase
      .from('profiles')
      .select('bodyweight')
      .eq('id', userId)
      .single();
    const bodyweight = Number(profileData?.bodyweight) || 0;

    // Fetch most recent strength_event per V1 lift — single batched query.
    // Include legacy lift_key variants (e.g. 'bench', 'squat') alongside new V1 keys.
    const LEGACY_KEYS = ['bench', 'squat', 'deadlift', 'pull-up', 'pull-ups', 'pullup', 'dead hang', 'deadhang'];
    const liftInputs: LiftInput[] = [];

    const { data: allRows } = await supabase
      .from('strength_events')
      .select('weight, reps, performed_at, canonical_name, lift_key')
      .eq('profile_id', userId)
      .or(`lift_key.in.(${[...V1_LIFT_POOL, ...LEGACY_KEYS].join(',')}),canonical_name.in.(${[...V1_LIFT_POOL].join(',')})`)
      .order('performed_at', { ascending: false });

    // Group by normalized V1 lift_key — handles legacy keys automatically
    const bestByLift = new Map<string, { weight: any; reps: any; performed_at: any; canonical_name: any; lift_key: any }>();
    for (const row of allRows ?? []) {
      // Normalize legacy key to V1 key
      const normalizedKey = normalizeToV1Lift(row.lift_key as string)
        ?? normalizeToV1Lift(row.canonical_name as string)
        ?? (row.lift_key as string);
      if (!bestByLift.has(normalizedKey)) {
        bestByLift.set(normalizedKey, { ...row, lift_key: normalizedKey });
      }
    }

    for (const liftKey of V1_LIFT_POOL) {
      const row = bestByLift.get(liftKey);
      if (!row) continue;

      const weight = Number(row.weight) || 0;
      const reps = Number(row.reps) || 0;

      let rawScore: number;
      if (liftKey === 'dead-hang') {
        rawScore = calculateDeadHangScore(reps, bodyweight);
      } else if (liftKey === 'weighted-pull-up') {
        const totalWeight = weight + bodyweight;
        rawScore = calculateE1RM(totalWeight, reps);
      } else {
        rawScore = calculateE1RM(weight, reps);
      }

      if (rawScore > 0) {
        liftInputs.push({
          lift: liftKey,
          rawScore,
          performedAt: row.performed_at,
          provenance: 'unverified',
        });
      }
    }

    if (liftInputs.length === 0 && bodyweight <= 0) {
      log.info('[LeaderboardRecompute] No lift data and no bodyweight, setting score to 0');
      await supabase
        .from('profiles')
        .update({ lifting_score: 0, duel_standard_strength_kg: 0, duel_standard_updated_at: new Date().toISOString() })
        .eq('id', userId);
      events.emit(EventTypes.LEADERBOARD_UPDATED, { userId, trigger, points: 0 });
      return 0;
    }

    const breakdown = calculateV1Score(liftInputs, bodyweight);
    const roundedScore = Math.round(breakdown.totalScore * 100) / 100;

    log.info('[LeaderboardRecompute] V1 score calculated:', {
      userId,
      liftCount: liftInputs.length,
      bodyweight,
      totalScore: roundedScore,
      capApplied: breakdown.capApplied,
      trigger,
    });

    // Update the profile with the new score
    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        lifting_score: roundedScore,
        duel_standard_strength_kg: roundedScore,
        duel_standard_updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (updateError) {
      log.error('[LeaderboardRecompute] Update error:', updateError);
      return null;
    }

    log.info({ userId, points: roundedScore, trigger });
    events.emit(EventTypes.LEADERBOARD_UPDATED, { userId, trigger, points: roundedScore });

    return roundedScore;
  } catch (error) {
    log.error('[LeaderboardRecompute] Unexpected error:', error);
    return null;
  }
}

// ─── Org Leaderboard (Phase 2D) ───────────────────────────────────────────────

/**
 * Fetch the leaderboard for a given org, scoped via organization_memberships.
 *
 * Source view: public_leaderboard_org_v1 (Phase 2D migration)
 *
 * Membership scoping:
 *   - Only status = 'active' members appear (enforced in the SQL view)
 *   - Excluded: 'inactive', 'invited', 'claim_pending'
 *   - No gym/home_gym_id contamination — purely org-membership-based
 *
 * This function does NOT modify or depend on the gym leaderboard path.
 *
 * @param orgId  - The organization UUID to scope the leaderboard to
 * @param client - Optional Supabase client override (defaults to singleton)
 * @returns      Array of OrgLeaderboardEntry ordered by rank ASC, or [] on error
 */
export async function fetchLeaderboardOrgV1(
  orgId: string,
  client: SupabaseClient = supabase,
): Promise<OrgLeaderboardEntry[]> {
  if (!orgId) return [];

  const cacheKey = `lb:org:${orgId}`;
  const cached = await cache.get<OrgLeaderboardEntry[]>(cacheKey);
  if (cached) return cached;

  return dedup(cacheKey, async () => {
    try {
      const { data, error } = await client
        .from('public_leaderboard_org_v1')
        .select(
          'org_id, user_id, handle, display_name, avatar_url, org_role, ' +
          'strength_score, freshness_days, freshness_band, ' +
          'wins, losses, total_duels, rank, gender, total_volume, bodyweight, created_at',
        )
        .eq('org_id', orgId)
        .order('rank', { ascending: true });

      if (error) {
        const msg = String(error.message ?? '');
        const isMissing = error.code === '42P01' || /does not exist/i.test(msg);
        if (isMissing) {
          log.warn('[OrgLeaderboard] public_leaderboard_org_v1 view not found — migration pending?', { orgId });
        } else {
          log.error('[OrgLeaderboard] query error', { orgId, message: msg });
        }
        return [];
      }

      const rows: OrgLeaderboardEntry[] = (data ?? []).map((r: any) => ({
        org_id: r.org_id,
        user_id: r.user_id,
        handle: r.handle ?? null,
        display_name: r.display_name ?? null,
        avatar_url: r.avatar_url ?? null,
        org_role: r.org_role,
        strength_score: r.strength_score ?? null,
        freshness_days: r.freshness_days ?? null,
        freshness_band: r.freshness_band ?? null,
        wins: r.wins ?? null,
        losses: r.losses ?? null,
        total_duels: r.total_duels ?? null,
        rank: r.rank ?? null,
        gender: r.gender ?? null,
        total_volume: r.total_volume ?? null,
        bodyweight: r.bodyweight ?? null,
        created_at: r.created_at ?? null,
      }));

      await cache.set(cacheKey, rows, 120_000); // 2-minute cache for org leaderboard
      return rows;
    } catch (err) {
      log.error('[OrgLeaderboard] unexpected error', { orgId, err });
      return [];
    }
  });
}

// Re-export OrgLeaderboardEntry for consumers of lib/leaderboard
export type { OrgLeaderboardEntry };
